import { BalloonsIqIcon, BaloonsIcon, Button, ColorBadge, Loading, SparkleIcon, TallBuildingIcon, Theme } from '@spoiler-alert/ui-library';
import PropTypes from 'prop-types';
import React from 'react';
import { createUseStyles } from 'react-jss';
import Tracking from '../../utilities/tracking-apis';
import ColorBadgeWithIcon from './color-badge-with-icon';
import TodoIcon from './icons/todo';
import RateChart from './rate-chart';

const useStyles = createUseStyles({
  taskContainer: {
    background:
      'linear-gradient(0deg, rgba(255, 255, 255, 0.75) 0%, rgba(255, 255, 255, 0.75) 100%), linear-gradient(90deg, #DAEDF1 0%, #F8F3D5 49.04%, #CBEED4 100%)',
    padding: '24px',
    minHeight: '128px',
  },
  iqHeader: {
    display: 'flex',
    alignItems: 'baseline',
  },
  logoSvg: {
    width: '105.502px',
    height: '16.256px',
  },
  iqSvg: {
    width: '25.085px',
    height: '24.854px',
  },
  rateCharts: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
  },
  taskContentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'start',
    marginBottom: '24px',
  },
  actionsLabel: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: '24px',
  },
  taskContent: {
    borderRadius: 6,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    border: `1px solid ${Theme.primaryButtonColor}`,
    background: Theme.white,
    boxShadow: '0px 0px 4.6px 0px rgba(42, 178, 74, 0.20)',
    height: '82px',
  },
  taskContentIq: {
    boxShadow: '0px 0px 4.6px 0px rgba(42, 178, 74, 0.20)',
    border: '2px solid transparent',
    background: `linear-gradient(white, white) padding-box, ${Theme.iqGradient} border-box`,
    borderRadius: 6,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    height: '82px',
    animation: '$iq-ripple 6s linear infinite',
    backgroundSize: '200%',
  },
  activeTitle: {
    fontSize: '18px !important',
    fontWeight: '700 !important',
  },
  disabledTitle: {
    fontSize: '16px !important',
    fontWeight: '500 !important',
  },
  taskActionHeader: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px',
    alignItems: 'center',
  },
  taskActionBar: {
    display: 'flex',
    flexDirection: 'row',
    gap: '32px',
  },
  taskActionMetrics: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  metric: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: '18px',
    color: Theme.green,
    letterSpacing: '-0.108px',
  },
  metricIq: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: '18px',
    backgroundImage: Theme.iqGradient,
    color: 'transparent',
    WebkitBackgroundClip: 'text',
    letterSpacing: '-0.108px',
  },
  metricTitle: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '18px',
    color: Theme.textColorSecondary,
    letterSpacing: '-0.084px',
  },
  iqButton: {
    height: '50px',
    fontWeight: 'bold',
  },
  iconWithLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  noAccessActionTitle: {
    marginLeft: '12px',
    fontSize: '14px',
  },
  iqBadge: {
    border: '2px solid transparent',
    background: `linear-gradient(${Theme.green5}, ${Theme.green5}) padding-box, ${Theme.iqGradient} border-box`,
    '& span': {
      backgroundImage: Theme.iqGradient,
      color: 'transparent',
      WebkitBackgroundClip: 'text',
    },
  },
  '@keyframes iq-ripple ': {
    '0%': {
      backgroundPosition: '0% 50%',
    },
    '50%': {
      backgroundPosition: '50% 100%',
    },
    '100%': {
      backgroundPosition: '0% 50%',
    },
  },
});

export const LandingPageTask = ({
  history,
  loading,
  actionEnabled,
  actionCompleted,
  actionTitle,
  actionIcon,
  actionMetrics,
  chartMetrics,
  button,
  hasAccess,
  featureEnabled,
  user,
  iqEnabled,
}) => {
  const classes = useStyles();

  if (loading)
    return (
      <div className={classes.taskContainer}>
        <div style={{ padding: '24px' }}>
          <Loading loading={loading} size="small" />
        </div>
      </div>
    );

  if (!hasAccess || !featureEnabled) {
    return (
      <div className={classes.taskContainer}>
        <div>
          <div className={classes.taskContentHeader}>
            <div className={classes.iconWithLabel}>
              <TodoIcon />
              <span className={classes.actionsLabel}>Actions</span>
            </div>
            {chartMetrics.length > 0 && (
              <div className={classes.rateCharts}>
                <RateChart
                  cutout={'75%'}
                  subjectColor={Theme.orange}
                  backgroundColor={Theme.orange30}
                  animation={true}
                  progress={Math.floor(chartMetrics[0].progress * 100)}
                  chartText={chartMetrics[0].chartText}
                  toolTipText={chartMetrics[0].toolTipText}
                />
              </div>
            )}
          </div>
          <div className={iqEnabled ? classes.taskContentIq : classes.taskContent}>
            <div className={classes.taskActionHeader}>
              <ColorBadge theme="grey">
                {hasAccess && !featureEnabled && <span>Feature Disabled</span>}
                {!hasAccess && (
                  <div className={classes.iconWithLabel}>
                    <TallBuildingIcon />
                    <span>Enterprise Only</span>
                  </div>
                )}
              </ColorBadge>
              <span className={classes.noAccessActionTitle}>{actionTitle}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!actionEnabled && !actionCompleted)
    return (
      <div className={classes.taskContainer}>
        <div>
          <div className={classes.taskContentHeader}>
            <div className={classes.iconWithLabel}>
              <TodoIcon />
              <span className={classes.actionsLabel}>Actions</span>
            </div>
          </div>
          <div className={iqEnabled ? classes.taskContentIq : classes.taskContent}>
            <div className={classes.taskActionHeader}>
              {actionIcon}
              <span className={classes.disabledTitle}>{actionTitle}</span>
            </div>
          </div>
        </div>
      </div>
    );

  const handleActionClick = () => {
    Tracking.Event(`${button.text} button clicked`, {
      buttonLink: button.link,
      timestamp: new Date().toISOString(),
      email: user?.email,
      transactionCycleId: user?.site?.transactionCycleId,
    });
    if (button.action) button.action();
    history.push(button.link);
  };

  return (
    <div className={classes.taskContainer}>
      <div>
        <div className={classes.taskContentHeader}>
          <div className={classes.iconWithLabel}>
            <TodoIcon />
            <span className={classes.actionsLabel}>Actions</span>
          </div>
          <div className={classes.rateCharts}>
            <RateChart
              cutout={'85%'}
              subjectColor={Theme.orange}
              backgroundColor={Theme.orange30}
              animation={true}
              progress={Math.floor(chartMetrics[0]?.progress * 100)}
              chartText={chartMetrics[0].chartText}
              toolTipText={chartMetrics[0].toolTipText}
            />
            <RateChart
              cutout={'85%'}
              subjectColor={Theme.green}
              backgroundColor={Theme.green30}
              animation={true}
              progress={Math.floor(chartMetrics[1]?.progress * 100)}
              chartText={chartMetrics[1].chartText}
              toolTipText={chartMetrics[1].toolTipText}
              iqEnabled={iqEnabled}
            />
          </div>
        </div>
        <div className={iqEnabled ? classes.taskContentIq : classes.taskContent}>
          <div className={classes.taskActionHeader}>
            {actionIcon}
            <span className={classes.activeTitle}>{actionTitle}</span>
            {actionCompleted && (
              <ColorBadgeWithIcon badgeClass={iqEnabled ? classes.iqBadge : {}} icon={iqEnabled ? BalloonsIqIcon : BaloonsIcon}>
                Complete
              </ColorBadgeWithIcon>
            )}
          </div>
          {!actionCompleted && (
            <div className={classes.taskActionBar}>
              <div className={classes.taskActionMetrics}>
                <span className={iqEnabled ? classes.metricIq : classes.metric}>{actionMetrics.value}</span>
                <span className={classes.metricTitle}>{actionMetrics.label}</span>
              </div>
              <Button iq={iqEnabled} onClick={handleActionClick} className={classes.iqButton} icon={SparkleIcon}>
                {button.text}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

LandingPageTask.propTypes = {
  history: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  actionTitle: PropTypes.string,
  actionIcon: PropTypes.node,
  actionMetrics: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }),
  chartMetrics: PropTypes.arrayOf(
    PropTypes.shape({
      progress: PropTypes.number,
      chartText: PropTypes.object,
      toolTipText: PropTypes.object,
    }),
  ),
  actionEnabled: PropTypes.bool,
  actionCompleted: PropTypes.bool,
  button: PropTypes.shape({
    text: PropTypes.string,
    link: PropTypes.string,
    action: PropTypes.func,
  }),
  hasAccess: PropTypes.bool,
  featureEnabled: PropTypes.bool,
  user: PropTypes.object,
  iqEnabled: PropTypes.bool,
};
